import { NetworkStatus, QueryResult, gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { MembershipTermAlertSearchFilter } from '@/graphql-types/globalTypes';

import {
  useMembersPagination_UserSearch,
  useMembersPagination_UserSearchVariables,
} from './graphql-types/useMembersPagination_UserSearch';
import {
  useMembersPagination_UserSearchCount,
  useMembersPagination_UserSearchCountVariables,
} from './graphql-types/useMembersPagination_UserSearchCount';

import { Member } from '../../MembersRecordList/components/Member';
import {
  IUsersQueryAction,
  IUsersQueryState,
  queryStateToFilter,
} from '../../usersQueryState';

const USER_SEARCH_QUERY = gql`
  query useMembersPagination_UserSearch($input: UserSearchInput!) {
    userSearch(input: $input) {
      results {
        ...Member_UserFragment
      }
    }
  }
  ${Member.userFragment}
`;

const USER_SEARCH_COUNT_QUERY = gql`
  query useMembersPagination_UserSearchCount($input: UserSearchCountInput!) {
    userSearchCount(input: $input)
  }
`;

const LIMIT = 20;

const queryStateToVariables = (
  queryState: IUsersQueryState,
  archivedOnly?: boolean
): useMembersPagination_UserSearchVariables => {
  return {
    input: {
      offset: 0,
      limit: LIMIT,
      filter: {
        ...queryStateToFilter(queryState, { archivedOnly }),
      },
    },
  };
};

const queryStateToCountVariables = (
  queryState: IUsersQueryState,
  archivedOnly?: boolean
): useMembersPagination_UserSearchCountVariables => {
  return {
    input: {
      filter: {
        ...queryStateToFilter(queryState, { archivedOnly }),
      },
    },
  };
};

const useQueryState = (): [
  IUsersQueryState,
  (newQuery: IUsersQueryState) => void
] => {
  const router = useRouter();

  const state: IUsersQueryState = {
    // @ts-ignore
    searchText: router.query.searchText || '',
    // page kept only for type compatibility
    page: 1,
    // @ts-ignore
    integrationIsMember: router.query.integrationIsMember,
    // @ts-ignore
    accountStatus: router.query.accountStatus
      ? Array.isArray(router.query.accountStatus)
        ? router.query.accountStatus
        : router.query.accountStatus
        ? [router.query.accountStatus]
        : []
      : [],
    // @ts-ignore
    membershipStatus: router.query.membershipStatus
      ? Array.isArray(router.query.membershipStatus)
        ? router.query.membershipStatus
        : router.query.membershipStatus
        ? [router.query.membershipStatus]
        : []
      : [],
    // @ts-ignore
    membershipType: router.query.membershipType
      ? Array.isArray(router.query.membershipType)
        ? router.query.membershipType
        : router.query.membershipType
        ? [router.query.membershipType]
        : []
      : [],
    // @ts-ignore
    role: router.query.role
      ? Array.isArray(router.query.role)
        ? router.query.role
        : [router.query.role]
      : [],
    termAlerts: (router.query.termAlerts as MembershipTermAlertSearchFilter[])
      ? Array.isArray(router.query.termAlerts)
        ? (router.query.termAlerts as MembershipTermAlertSearchFilter[])
        : ([router.query.termAlerts] as MembershipTermAlertSearchFilter[])
      : [],
  };

  return [
    state,
    (query: IUsersQueryState) => {
      router.replace({
        query: {
          ...router.query,
          ...query,
        },
      });
    },
  ];
};

export type IUseMembersPagination = {
  isLoading: boolean;
  isCountLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  queryState: IUsersQueryState;
  dispatch: (action: IUsersQueryAction) => void;
  totalCount: number;
  userSearchResults: Pick<
    QueryResult<
      useMembersPagination_UserSearch,
      useMembersPagination_UserSearchVariables
    >,
    'data' | 'networkStatus' | 'error' | 'loading'
  >;
  limit: number;
  archivedOnly?: boolean;
};

export const useMembersPagination = (options?: {
  archivedOnly?: boolean;
}): IUseMembersPagination => {
  const archivedOnly = !!options?.archivedOnly;
  const [queryState, dispatch] = useQueryState();

  const variables = useMemo(
    () => queryStateToVariables(queryState, archivedOnly),
    [queryState, archivedOnly]
  );
  const countVariables = useMemo(
    () => queryStateToCountVariables(queryState, archivedOnly),
    [queryState, archivedOnly]
  );

  const { fetchMore, ...userSearchResults } = useQuery<
    useMembersPagination_UserSearch,
    useMembersPagination_UserSearchVariables
  >(USER_SEARCH_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const userSearchCountResults = useQuery<
    useMembersPagination_UserSearchCount,
    useMembersPagination_UserSearchCountVariables
  >(USER_SEARCH_COUNT_QUERY, {
    variables: countVariables,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const results = userSearchResults.data?.userSearch?.results ?? [];
  const totalCount = userSearchCountResults.data?.userSearchCount ?? 0;

  const isLoading =
    userSearchResults.networkStatus === NetworkStatus.loading ||
    userSearchResults.networkStatus === NetworkStatus.setVariables;

  const isCountLoading =
    userSearchCountResults.networkStatus === NetworkStatus.loading ||
    userSearchCountResults.networkStatus === NetworkStatus.setVariables;

  const isFetchingMore =
    userSearchResults.networkStatus === NetworkStatus.fetchMore;

  const hasMore =
    !isCountLoading && results.length > 0 && results.length < totalCount;

  const loadMore = useCallback(async () => {
    if (isLoading || isFetchingMore || isCountLoading || !hasMore) return;

    await fetchMore({
      variables: {
        input: {
          ...variables.input,
          offset: results.length,
          limit: LIMIT,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const merged = [
          ...prev.userSearch.results,
          ...fetchMoreResult.userSearch.results,
        ];

        const uniqueResults = merged.filter(
          (item, index, self) =>
            index === self.findIndex((x) => x._id === item._id)
        );

        return {
          ...prev,
          userSearch: {
            ...fetchMoreResult.userSearch,
            results: uniqueResults,
          },
        };
      },
    });
  }, [
    fetchMore,
    variables.input,
    results.length,
    hasMore,
    isLoading,
    isFetchingMore,
    isCountLoading,
  ]);

  return {
    isLoading,
    isCountLoading,
    isFetchingMore,
    hasMore,
    loadMore,
    queryState,
    dispatch,
    totalCount,
    userSearchResults,
    limit: LIMIT,
    archivedOnly,
  };
};
